// frontend/src/pages/AuthCallback.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";

/**
 * AuthCallback.jsx (Supabase Auth)
 * - Reads Supabase OAuth / magic-link tokens from URL hash
 * - Sets Supabase session
 * - Marks app as authed (for existing MVP gating)
 * - Redirects to /dashboard (or ?next=)
 *
 * REQUIRED env vars (frontend/.env):
 *   REACT_APP_SUPABASE_URL=...
 *   REACT_APP_SUPABASE_ANON_KEY=...
 */

const STORAGE_KEYS = {
  AUTHED: "aifine_authed",
  USER: "aifine_user",
};

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeJsonSet(key, obj) {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
  } catch {
    // ignore
  }
}

function getNextFromSearch(search) {
  try {
    const params = new URLSearchParams(search || "");
    const raw = params.get("next");
    if (!raw) return "/dashboard";

    const decoded = decodeURIComponent(raw);
    if (typeof decoded !== "string") return "/dashboard";
    if (!decoded.startsWith("/")) return "/dashboard";
    if (decoded.startsWith("//")) return "/dashboard";
    return decoded;
  } catch {
    return "/dashboard";
  }
}

function parseHashTokens() {
  // Supabase returns tokens in URL hash: #access_token=...&refresh_token=...&expires_in=...
  const hash = window.location.hash || "";
  const h = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(h);

  const access_token = params.get("access_token") || "";
  const refresh_token = params.get("refresh_token") || "";
  const expires_in = params.get("expires_in") || "";
  const token_type = params.get("token_type") || "";

  return { access_token, refresh_token, expires_in, token_type };
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [note, setNote] = useState("Authenticating…");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
      const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
        setNote("Missing Supabase env vars (REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY).");
        // Hard fail → send user back to auth
        window.setTimeout(() => navigate("/auth", { replace: true }), 800);
        return;
      }

      const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

      const { access_token, refresh_token } = parseHashTokens();

      if (!access_token || !refresh_token) {
        // Nothing to set → user likely hit callback directly
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        navigate("/auth", { replace: true });
        return;
      }

      try {
        setNote("Finalizing session…");

        const { data, error } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });

        if (error) {
          throw error;
        }

        // Mark "authed" for the current MVP gating logic
        safeSet(STORAGE_KEYS.AUTHED, "true");

        // Optional: store minimal user object for tier logic later
        const u = data?.user || null;
        if (u) {
          safeJsonSet(STORAGE_KEYS.USER, {
            supabase_user_id: u.id,
            email: u.email || null,
            updated_at_iso: new Date().toISOString(),
          });
        }

        // Clear hash (so refresh doesn’t re-run)
        window.history.replaceState(null, "", window.location.pathname + window.location.search);

        // Route to next (or dashboard)
        const nextPath = getNextFromSearch(window.location.search);
        navigate(nextPath || "/dashboard", { replace: true });
      } catch (err) {
        // Clean up
        try {
          safeSet(STORAGE_KEYS.AUTHED, "false");
        } catch {}
        setNote("Authentication failed. Returning to login…");
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        window.setTimeout(() => navigate("/auth", { replace: true }), 900);
      }
    };

    run();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 rounded-full border-4 border-slate-200 border-t-slate-900 animate-spin" />
        <p className="text-slate-700 font-semibold">{note}</p>
      </div>
    </div>
  );
}